import { useMemo, useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, EmptyState, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { useLiveAction } from '../lib/data.ts';
import { usePlaces } from '../lib/places.tsx';
import { clock, elapsed, minutesSince, qty } from '../lib/format.ts';
import { device } from '../lib/session.tsx';
import type { Ticket } from '../lib/types.ts';
import { useNow, useUi } from '../lib/ui-state.tsx';

interface KitchenTicket extends Ticket {
  /** Warnings for the whole order, such as a guest's allergy. */
  alerts?: string[];
  items: { id: string; name: string; qty: number; modifiers: string[]; note: string | null; course: number; void: boolean; void_reason: string | null }[];
}
interface KitchenData {
  stations: { id: string; name: string; printer?: string | null }[];
  tickets: KitchenTicket[];
}

type State = Ticket['state'];
const STATE: Record<State, { tone: Tone; icon: IconName; label: MessageKey }> = {
  new: { tone: 'warn', icon: 'bell', label: 'kitchen.state_new' },
  preparing: { tone: 'info', icon: 'flame', label: 'kitchen.state_preparing' },
  ready: { tone: 'ok', icon: 'check', label: 'kitchen.state_ready' },
  served: { tone: 'neutral', icon: 'checkCircle', label: 'kitchen.state_served' },
  cancelled: { tone: 'neutral', icon: 'x', label: 'kitchen.state_cancelled' },
};
/** The one obvious next step for each ticket, and the step back if tapped by mistake. */
const NEXT: Partial<Record<State, { to: State; label: MessageKey; icon: IconName }>> = {
  new: { to: 'preparing', label: 'kitchen.start', icon: 'flame' },
  preparing: { to: 'ready', label: 'kitchen.ready', icon: 'bell' },
  ready: { to: 'served', label: 'kitchen.served', icon: 'check' },
};
const BACK: Partial<Record<State, State>> = { preparing: 'new', ready: 'preparing', served: 'ready' };

/** Waiting time turns amber after 10 minutes and red after 20. */
function waitTone(mins: number): Tone {
  return mins >= 20 ? 'danger' : mins >= 10 ? 'warn' : 'neutral';
}

export function Kitchen() {
  const { fail, toast } = useUi();
  const now = useNow(15_000);
  const [station, setStationState] = useState<string | null>(device.station);
  const [showServed, setShowServed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const { here, multi } = usePlaces();
  // Each kitchen sees the tickets of its own location.
  const at = multi ? (here?.id ?? null) : null;
  const input = useMemo(() => ({ ...(station ? { station } : {}), ...(at ? { location: at } : {}), include_served: showServed }), [station, at, showServed]);
  const { data, setData, live } = useLiveAction<KitchenData>('restaurant.kitchen_tickets', input, ['restaurant']);

  const setStation = (id: string | null) => {
    device.station = id;
    setStationState(id);
  };

  const move = async (ticket: KitchenTicket, to: State) => {
    setBusy(ticket.id);
    // Show the change at once; the live update confirms it.
    setData((d) => (d ? { ...d, tickets: d.tickets.map((x) => (x.id === ticket.id ? { ...x, state: to } : x)) } : d));
    try {
      await action('restaurant.ticket_state', { ticket: ticket.id, state: to });
    } catch (err) {
      fail(err);
      setData((d) => (d ? { ...d, tickets: d.tickets.map((x) => (x.id === ticket.id ? ticket : x)) } : d));
    } finally {
      setBusy(null);
    }
  };

  const tickets = (data?.tickets ?? []).filter((x) => showServed || x.state !== 'served');
  const columns: State[] = showServed ? ['new', 'preparing', 'ready', 'served'] : ['new', 'preparing', 'ready'];
  const stationName = (id: string) => data?.stations.find((s) => s.id === id)?.name ?? '';
  const hasPrinter = (id: string) => !!data?.stations.find((s) => s.id === id)?.printer;
  const reprint = async (tk: KitchenTicket) => {
    try {
      await action('printing.reprint_ticket', { ticket: tk.id });
      toast(t('printing.reprinted'));
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Shell live={live}>
      <div className="page kitchen-page">
        <div className="page-head">
          <div>
            <h1>{station ? stationName(station) || t('kitchen.title') : t('kitchen.title')}</h1>
            <p className="muted">
              {data ? tn('kitchen.open_count', tickets.filter((x) => x.state !== 'served').length) : <Skeleton w={140} />}
            </p>
          </div>
          <div className="page-actions">
            <button className={`chip${showServed ? ' active' : ''}`} aria-pressed={showServed} onClick={() => setShowServed((v) => !v)}>
              <Icon name="undo" size={16} /> {t('kitchen.show_served')}
            </button>
          </div>
        </div>

        <div className="toolbar">
          <div className="segmented" role="tablist" aria-label={t('kitchen.station')}>
            <button role="tab" aria-selected={!station} className={!station ? 'active' : ''} onClick={() => setStation(null)}>
              {t('kitchen.all_stations')}
            </button>
            {(data?.stations ?? []).map((s) => (
              <button key={s.id} role="tab" aria-selected={station === s.id} className={station === s.id ? 'active' : ''} onClick={() => setStation(s.id)}>
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          <div className="kds">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} h={320} r="var(--radius-lg)" />
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState icon="chef" title={t('kitchen.empty')} body={t('kitchen.empty_body')} />
        ) : (
          <div className="kds" style={{ ['--kds-cols' as string]: columns.length }}>
            {columns.map((col) => {
              const list = tickets.filter((x) => x.state === col);
              const s = STATE[col];
              return (
                <section key={col} className="kds-col" aria-label={t(s.label)}>
                  <h2 className="kds-col-head">
                    <StatusBadge tone={s.tone} icon={s.icon} label={t(s.label)} />
                    <span className="count">{list.length}</span>
                  </h2>
                  {list.map((tk) => {
                    const mins = minutesSince(tk.sent_at, now);
                    const tone = col === 'ready' || col === 'served' ? 'neutral' : waitTone(mins);
                    const next = NEXT[col];
                    const back = BACK[col];
                    return (
                      <article key={tk.id} className={`ticket wait-${tone}`} aria-busy={busy === tk.id || undefined}>
                        <header className="ticket-head">
                          <div>
                            <strong className="ticket-table">{tk.table_name}</strong>
                            <span className="muted">
                              #{tk.number}
                              {!station ? ` · ${stationName(tk.station)}` : ''}
                            </span>
                          </div>
                          <span className="ticket-tools">
                            <span className={`ticket-timer text-${tone === 'neutral' ? 'muted' : tone}`} title={clock(tk.sent_at)}>
                              <Icon name="clock" size={14} /> {elapsed(tk.sent_at, now)}
                            </span>
                            {hasPrinter(tk.station) ? (
                              <IconButton icon="printer" label={t('printing.reprint')} className="ticket-print" onClick={() => void reprint(tk)} />
                            ) : null}
                          </span>
                        </header>
                        {tk.alerts?.length ? (
                          <div className="ticket-alerts" role="alert">
                            {tk.alerts.map((a) => (
                              <span key={a}>
                                <Icon name="alert" size={14} /> {a}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <ul className="ticket-items">
                          {tk.items.map((it) => (
                            <li key={it.id} className={it.void ? 'void' : ''}>
                              <span className="ticket-qty">{qty(it.qty)}</span>
                              <span>
                                <span className="ticket-name">{it.name}</span>
                                {it.modifiers.length ? <span className="ticket-mods">{it.modifiers.join(', ')}</span> : null}
                                {it.note ? (
                                  <span className="ticket-note">
                                    <Icon name="note" size={12} /> {it.note}
                                  </span>
                                ) : null}
                                {it.void ? (
                                  <span className="ticket-void">
                                    <Icon name="x" size={12} /> {t('kitchen.removed', { reason: it.void_reason ?? '' })}
                                  </span>
                                ) : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <footer className="ticket-foot">
                          {back ? (
                            <Button variant="ghost" size="sm" icon="undo" onClick={() => void move(tk, back)} disabled={busy === tk.id}>
                              {t('kitchen.back')}
                            </Button>
                          ) : (
                            <span />
                          )}
                          {next ? (
                            <Button
                              variant={col === 'preparing' ? 'success' : 'primary'}
                              size="lg"
                              icon={next.icon}
                              onClick={() => void move(tk, next.to)}
                              disabled={busy === tk.id}
                            >
                              {t(next.label)}
                            </Button>
                          ) : null}
                        </footer>
                      </article>
                    );
                  })}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
